import { useMemo, useState } from 'react'
import { EventNames } from '../../../../../types/misc.types'
import MFAccordion from '../../../../ui-components/MFAccordion'
import { trackEvent } from '../../../../../utils/appBridge'
import { usePreloadImages } from '../../../../../utils/image-loading/usePreloadImages'
import AccordionHeaderContentWithTag from '../../../../AccordionHeaderContentWithTag'
import { EliteLandingPageRemark } from '../../../EliteLandingPageRemark'
import { getAppVersion } from '../../../../../utils/sessionStorage'
import { GROUPED_STATIC_TEXT_ROLE } from '../../../../ui-components/NameValueTableRow'
import type { EliteEventExtras } from '../../../../../utils/eliteEventExtras'

// Extras arrive via prop (not context) because this renders inside ViewAllBenefitsBottomSheet
// (a portal), where context does not reach. In-page parents pass the screen's extras.
export const AlsoUnlockEliteScores = ({ buyCtaExtras = {} }: { buyCtaExtras?: EliteEventExtras }) => {
  const [isAccordionOpen, setIsAccordionOpen] = useState(false)
  const [outsideAssetWidth, setOutsideAssetWidth] = useState(0)
  const assetRatio = 0.196
  const isV47OrAbove = getAppVersion() >= 47

  const handleAccordionClick = () => {
    setIsAccordionOpen((isAccordionOpenPrev) => {
      trackEvent({
        eventName: EventNames.UserClicked,
        payload: {
          ...buyCtaExtras,
          eventId: 'Power Rank Trends',
          responseName: !isAccordionOpenPrev ? 'Expand' : 'Collapse',
          screenType: window?.location?.href?.includes('elite-dashboard')
            ? 'MF_L2_Elite_Dashboard_Bottomsheet'
            : 'MF_Elite_Landing'
        }
      })
      return !isAccordionOpenPrev
    })
  }

  const powerRankTrendsImage = isV47OrAbove
    ? '/images/elite/also_unlock_elite_scores_v2.svg'
    : '/images/elite/also_unlock_elite_scores.svg'

  usePreloadImages([powerRankTrendsImage])

  const bgAssetHeight = useMemo(() => {
    return outsideAssetWidth * (assetRatio ?? 1) || 700
  }, [outsideAssetWidth])

  const returnsScoreLabel = isV47OrAbove ? 'Consistency Score' : 'Returns Score'
  const stabilityScoreLabel = isV47OrAbove ? 'Volatility Score' : 'Stability Score'

  return (
    <div className='bg-white'>
      <MFAccordion
        isExpanded={isAccordionOpen}
        onAccordionClick={handleAccordionClick}
        title={'Power Rank Trends'}
        accessibleName='Also unlock Elite Scores, Understand why a fund ranks the way it does.'
        middleContent={
          <AccordionHeaderContentWithTag
            title={'Also unlock Elite Scores'}
            titleClassName='!text-black !text-[14px] !font-[500]'
            value={'Understand why a fund ranks the way it does.'}
            valueClassName='!text-[#6A6E6E] !text-[13px] !font-[400] pt-1 leading-[150%]'
          />
        }
        middleContentClassName='!pr-3'
        accordionHeaderClassName={`!px-0 !py-0`}
        accordionBodyClassName='!px-0'
        isBorderTop={false}
      >
        <div className='flex w-full justify-center pt-3 pb-5'>
          <div className='flex flex-col w-full'>
            <div
              ref={(ref) => {
                if (ref && outsideAssetWidth === 0) {
                  setOutsideAssetWidth(ref?.clientWidth ?? 300)
                }
              }}
              style={{ height: bgAssetHeight }}
              className='w-full h-max'
            >
              <img
                src={powerRankTrendsImage}
                className='w-full h-auto'
                alt='Illustration of Elite Scores breakdown'
              />
            </div>
            <ul className='list-disc pl-4 pt-6 m-0'>
              <li className='mb-2 list-disc'>
                <div
                  role={GROUPED_STATIC_TEXT_ROLE}
                  aria-label={`${returnsScoreLabel}, How consistently a fund has delivered returns over the long term`}
                >
                  <span aria-hidden='true'>
                    <b>{returnsScoreLabel} – </b> How consistently a fund has delivered returns over the long term
                  </span>
                </div>
              </li>
              <li className='mb-2 list-disc'>
                <div
                  role={GROUPED_STATIC_TEXT_ROLE}
                  aria-label={`${stabilityScoreLabel}, How volatile the fund is compared to the overall market`}
                >
                  <span aria-hidden='true'>
                    <b>{stabilityScoreLabel} – </b>
                    How volatile the fund is compared to the overall market
                  </span>
                </div>
              </li>
              <li className='mb-2 list-disc'>
                <div
                  role={GROUPED_STATIC_TEXT_ROLE}
                  aria-label={`Recency Score, The fund's recent performance over the last 2 years`}
                >
                  <span aria-hidden='true'>
                    <b>Recency Score – </b> The fund's recent performance over the last 2 years
                  </span>
                </div>
              </li>
            </ul>
            <div className='pt-4'>
              <div
                role={GROUPED_STATIC_TEXT_ROLE}
                aria-label='Use these to pick funds that match your comfort with risk and return'
              >
                <div aria-hidden='true'>
                  <EliteLandingPageRemark>
                    <div>
                      Use these to pick funds that match your comfort with{' '}
                      <span className='font-[500]'> risk and return </span>
                    </div>
                  </EliteLandingPageRemark>
                </div>
              </div>
            </div>
          </div>
        </div>
      </MFAccordion>
    </div>
  )
}
